import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  onIdTokenChanged,
  User,
  UserCredential,
} from 'firebase/auth';
import { getFirebaseAuth } from './firebase';
import { ALLOWED_EMAIL_DOMAIN, isAllowedEmail } from '../config/access';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

/**
 * Create an account and send the verification email. Access to any feedback
 * starts once the address is verified (see `loadOrCreateUserProfile`).
 * Non-company emails are refused up front with a clear message; the security
 * rules would refuse them everything anyway.
 */
export const signUp = async (email: string, password: string): Promise<UserCredential> => {
  if (!isAllowedEmail(email)) {
    throw Object.assign(new Error(`Use your @${ALLOWED_EMAIL_DOMAIN} email address.`), {
      code: 'markup/wrong-domain',
    });
  }
  const credential = await createUserWithEmailAndPassword(getFirebaseAuth(), email, password);
  await sendEmailVerification(credential.user);
  return credential;
};

export const signIn = (email: string, password: string): Promise<UserCredential> =>
  signInWithEmailAndPassword(getFirebaseAuth(), email, password);

export const resendVerification = async (): Promise<void> => {
  const user = getFirebaseAuth().currentUser;
  if (user && !user.emailVerified) await sendEmailVerification(user);
};

export const resetPassword = (email: string): Promise<void> =>
  sendPasswordResetEmail(getFirebaseAuth(), email);

export const logOut = (): Promise<void> => signOut(getFirebaseAuth());

/**
 * Fires on every sign-in, not just when the uid changes. `onAuthStateChanged`
 * stays silent when the same user signs in again — e.g. a new account, still
 * signed in from sign-up, signing in after verifying its email — which left
 * the profile unloaded and the form looking hung.
 */
export const onAuthChange = (callback: (user: User | null) => void) =>
  onIdTokenChanged(getFirebaseAuth(), callback);

export const getCurrentUser = (): User | null => getFirebaseAuth().currentUser;

/** Firebase auth error codes → something a client can act on. */
export const describeAuthError = (error: any): string => {
  switch (error?.code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'That email and password don’t match.';
    case 'auth/email-already-in-use':
      return 'There’s already an account for that email — sign in instead.';
    case 'auth/weak-password':
      return 'Please use a password of at least 6 characters.';
    case 'auth/invalid-email':
      return 'That doesn’t look like an email address.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a minute and try again.';
    case 'markup/wrong-domain':
      return error.message;
    case 'auth/network-request-failed':
      return 'Couldn’t reach the server. Check your connection.';
    default:
      return error?.message || 'Something went wrong. Please try again.';
  }
};
